import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getParentTopicDisplayName } from '../utils/categoryGrouping';

interface HistoryQuestion {
  category: string;
  parentTopic: string;
  questionText: string;
  optionA: string | null;
  optionB: string | null;
  optionC: string | null;
  optionD: string | null;
  correctAnswer: string;
  selectedAnswer: string | null;
  isCorrect: boolean | null;
}

interface HistoryEntry {
  date: string;
  participated: boolean;
  score: number | null;
  questionsCorrect: number | null;
  totalQuestions: number;
  completionTime: number | null;
  questions: HistoryQuestion[];
}

const OPTION_KEYS: Array<'A' | 'B' | 'C' | 'D'> = ['A', 'B', 'C', 'D'];

// Calculate current quiz date (same logic as server - GMT+10)
const getCurrentQuizDate = (): string => {
  const now = new Date();
  const offsetMs = 10 * 60 * 60 * 1000; // GMT+10 offset
  const msPerDay = 24 * 60 * 60 * 1000;
  const nowInTargetZone = now.getTime() + offsetMs;
  const startOfDay = Math.floor(nowInTargetZone / msPerDay) * msPerDay;
  const dateInTargetZone = new Date(startOfDay);
  
  const year = dateInTargetZone.getUTCFullYear();
  const month = String(dateInTargetZone.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dateInTargetZone.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Calculate countdown to next quiz reset
const getResetCountdown = (): string => {
  const now = Date.now();
  const offsetMs = 10 * 60 * 60 * 1000; // GMT+10 offset
  const msPerDay = 24 * 60 * 60 * 1000;
  const nowInTargetZone = now + offsetMs;
  const startOfDay = Math.floor(nowInTargetZone / msPerDay) * msPerDay;
  const nextMidnight = startOfDay + msPerDay;
  const timeUntilReset = Math.max(0, nextMidnight - nowInTargetZone);

  const totalSeconds = Math.floor(timeUntilReset / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((unit) => unit.toString().padStart(2, '0'))
    .join(':');
};

const PremiumQuizzes: React.FC = () => {
  useDocumentTitle('Cognidaily | Premium Quiz History');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [resetCountdown, setResetCountdown] = useState<string>('--:--:--');

  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(totalAvailable / pageSize));
  const currentQuizDate = getCurrentQuizDate();

  const formatOrdinal = (value: number) => {
    const mod100 = value % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
    switch (value % 10) {
      case 1:
        return `${value}st`;
      case 2:
        return `${value}nd`;
      case 3:
        return `${value}rd`;
      default:
        return `${value}th`;
    }
  };

  const formatDisplayDate = (value: string) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(date);
    const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(date);
    const day = formatOrdinal(date.getDate());
    const year = new Intl.DateTimeFormat('en-GB', { year: 'numeric' }).format(date);
    return `${weekday}, ${day} of ${month} ${year}`;
  };

  useEffect(() => {
    const updateCountdown = () => {
      setResetCountdown(getResetCountdown());
    };

    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get<{
          success: boolean;
          data: { history: HistoryEntry[]; totalAvailable: number };
          message?: string;
        }>('/api/quiz/history', {
          params: { limit: pageSize, offset: (page - 1) * pageSize, quizType: 'main' }
        });

        if (!res.data.success) {
          throw new Error(res.data.message || 'Failed to load history');
        }
        // Filter out the current active quiz to prevent access
        const filteredHistory = (res.data.data.history || []).filter(
          (entry) => entry.date !== currentQuizDate
        );
        setHistory(filteredHistory);
        // Adjust total available count (subtract 1 if current quiz was in the list)
        const originalTotal = res.data.data.totalAvailable || 0;
        const hadCurrentQuiz = (res.data.data.history || []).some(
          (entry) => entry.date === currentQuizDate
        );
        setTotalAvailable(hadCurrentQuiz ? Math.max(0, originalTotal - 1) : originalTotal);
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Failed to load quiz history.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
    setExpandedDate(null);
  }, [page, currentQuizDate]);

  if (!user?.isPremium) {
    return (
      <div className="premium-history-page">
        <div className="premium-history-container">
          <div className="premium-history-card premium-history-card--centered">
            <h2>Premium members only</h2>
            <p className="premium-history-muted">
              Upgrade to Premium to unlock your full competitive quiz history.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/premium')}>
              View Premium
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-history-page">
      <div className="premium-history-container">
        <div className="premium-history-card">
          <Link to="/compete" className="premium-history-back">
            ← Back to Compete
          </Link>
          <header className="premium-history-header">
            <div className="premium-history-header__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v12H4z"></path>
                <path d="M8 20h8"></path>
                <path d="M12 16v4"></path>
              </svg>
            </div>
            <div className="premium-history-header__content">
              <h1>Competitive Quiz History</h1>
            </div>
          </header>

          {error && (
            <div className="premium-history-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="premium-history-body">
            {/* Current Quiz Countdown Notice */}
            <div className="premium-history-current-quiz-notice">
              <div className="premium-history-current-quiz-notice__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className="premium-history-current-quiz-notice__content">
                <h3>Today&apos;s Quiz</h3>
                <p>
                  Today&apos;s quiz will become available in <strong>{resetCountdown}</strong> (at midnight GMT+10).
                </p>
              </div>
            </div>

            {loading ? (
              <div className="premium-history-empty">Loading quiz history...</div>
            ) : history.length === 0 ? (
              <div className="premium-history-empty">No quiz history yet.</div>
            ) : (
              <div className="premium-history-list">
                {history.map((entry) => {
                  const isExpanded = expandedDate === entry.date;
                  return (
                    <article key={entry.date} className={`premium-history-row ${isExpanded ? 'is-expanded' : ''}`}>
                      <button
                        type="button"
                        className="premium-history-summary"
                        onClick={() => setExpandedDate(isExpanded ? null : entry.date)}
                        aria-expanded={isExpanded}
                      >
                        <div className="premium-history-summary__date">
                          <h3>{formatDisplayDate(entry.date)}</h3>
                          {entry.participated && (
                            <span className="premium-history-badge">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              Played
                            </span>
                          )}
                        </div>
                        <div className="premium-history-summary__meta">
                          <span className="premium-history-summary__questions">{entry.questions.length} questions</span>
                          <span className="premium-history-summary__toggle">
                            {isExpanded ? 'Hide questions' : 'View questions'}
                          </span>
                        </div>
                      </button>
                      <div className={`premium-history-details ${isExpanded ? 'is-expanded' : ''}`}>
                        <div className="premium-history-details__inner">
                          {entry.questions.map((question, index) => {
                            const correctKey = question.correctAnswer?.toUpperCase() || '';
                            const selectedKey = question.selectedAnswer?.toUpperCase() || '';
                            return (
                              <div key={`${entry.date}-${index}`} className="premium-history-question">
                                <div className="premium-history-question__header">
                                  <span className="premium-history-question__index">Q{index + 1}</span>
                                  <div>
                                    <p className="premium-history-question__text">{question.questionText}</p>
                                    <span className="premium-history-question__topic">
                                      Topic: {getParentTopicDisplayName(question.parentTopic) || question.parentTopic} - {question.category}
                                    </span>
                                  </div>
                                </div>
                                <div className="premium-history-options">
                                  {OPTION_KEYS.map((key) => {
                                    const optionText =
                                      key === 'A'
                                        ? question.optionA
                                        : key === 'B'
                                          ? question.optionB
                                          : key === 'C'
                                            ? question.optionC
                                            : question.optionD;
                                    if (!optionText) {
                                      return null;
                                    }
                                    const isCorrect = key === correctKey;
                                    const isSelected = key === selectedKey;
                                    return (
                                      <div
                                        key={`${entry.date}-${index}-${key}`}
                                        className={`premium-history-option ${
                                          isCorrect ? 'is-correct' : ''
                                        } ${isSelected && !isCorrect ? 'is-selected' : ''}`}
                                      >
                                        <span>{key}.</span>
                                        <span>{optionText}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          <div className="premium-history-footer">
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1 || loading}
            >
              Previous
            </button>
            <span className="premium-history-footer__count">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumQuizzes;
